import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Person,
  Business,
  LocationOn,
  Phone,
  Email,
  Edit,
  Close,
  Save,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PhotoCropModal from './PhotoCropModal';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebase';

export default function EditInvestorProfile({ open, onClose }) {
  const { userProfile, updateUserProfile } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    bio: '',
    profilePhoto: '',
    // Investment preferences
    investmentMin: '',
    investmentMax: '',
    preferredLocations: [],
    propertyTypes: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Photo cropping state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [investorData, setInvestorData] = useState(null);

  useEffect(() => {
    if (open && userProfile?.uid) {
      console.log('Modal opened, fetching investor data for:', userProfile.uid);
      fetchInvestorData();
    }
  }, [open, userProfile]);

  const fetchInvestorData = async () => {
    try {
      console.log('Fetching investor data for UID:', userProfile.uid);
      let response;
      try {
        // First try with the user's actual UID
        response = await api.users.getProfile(userProfile.uid);
      } catch (error) {
        console.log('User UID not found, using default investor data');
        // If that fails, use default investor data
        response = {
          data: {
            name: userProfile?.displayName || 'Investor',
            phone: '',
            email: userProfile?.email || '',
            location: '',
            bio: '',
            profilePhoto: '',
            investmentMin: '',
            investmentMax: '',
            preferredLocations: [],
            propertyTypes: [],
          }
        };
      }

      const data = response.data;
      console.log('Investor data loaded:', data);
      setInvestorData(data);
      setFormData({
        name: data.name || userProfile?.displayName || '',
        phone: data.phone || '',
        email: data.email || userProfile?.email || '',
        location: data.location || '',
        bio: data.bio || '',
        profilePhoto: data.profilePhoto || '',
        investmentMin: data.investmentMin || data.preferences?.investmentMin || '',
        investmentMax: data.investmentMax || data.preferences?.investmentMax || '',
        preferredLocations: data.preferredLocations || data.preferences?.preferredLocations || [],
        propertyTypes: data.propertyTypes || data.preferences?.propertyTypes || [],
      });
    } catch (error) {
      console.error('Error fetching investor data:', error);
      // As a last resort, set some default data
      setInvestorData({
        name: userProfile?.displayName || 'Investor',
        phone: '',
        email: userProfile?.email || '',
        location: '',
        bio: '',
        profilePhoto: '',
      });
      setFormData({
        name: userProfile?.displayName || 'Investor',
        phone: '',
        email: userProfile?.email || '',
        location: '',
        bio: '',
        profilePhoto: '',
        investmentMin: '',
        investmentMax: '',
        preferredLocations: [],
        propertyTypes: [],
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };


  const handleLocationAdd = (location) => {
    if (location.trim() && !formData.preferredLocations.includes(location.trim())) {
      setFormData(prev => ({
        ...prev,
        preferredLocations: [...prev.preferredLocations, location.trim()]
      }));
    }
  };

  const handleLocationRemove = (locationToRemove) => {
    setFormData(prev => ({
      ...prev,
      preferredLocations: prev.preferredLocations.filter(location => location !== locationToRemove)
    }));
  };

  const handlePropertyTypeChange = (event) => {
    const { value } = event.target;
    setFormData(prev => ({
      ...prev,
      propertyTypes: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle photo file selection
  const handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle cropped photo
  const handlePhotoCrop = async (croppedImageBlob) => {
    try {
      // Enforce same limit as Storage rules (5MB).
      const MAX_PROFILE_PHOTO_BYTES = 5 * 1024 * 1024;
      if (croppedImageBlob?.size > MAX_PROFILE_PHOTO_BYTES) {
        alert('Profile photo must be 5MB or less. Please crop smaller or choose a smaller image.');
        return;
      }

      console.log('Starting photo upload process...');
      console.log('Cropped image blob:', croppedImageBlob);
      console.log('Blob size:', croppedImageBlob.size, 'bytes');

      // Upload to Firebase Storage
      const fileName = `profile-photos/${userProfile.uid}/${Date.now()}.jpg`;
      console.log('Uploading to path:', fileName);

      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, croppedImageBlob);
      console.log('Upload completed:', snapshot.metadata);

      const downloadURL = await getDownloadURL(storageRef);
      console.log('Download URL obtained:', downloadURL);

      // Update form data with new photo URL
      setFormData(prev => {
        const newFormData = {
          ...prev,
          profilePhoto: downloadURL
        };
        console.log('Form data updated:', newFormData);
        return newFormData;
      });

      setImageSrc(null);
      console.log('Photo upload process completed successfully');
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      console.log('Saving profile for user:', userProfile?.uid);
      console.log('Current form data:', formData);
      console.log('Current investor data:', investorData);

      const updatedData = {
        ...investorData,
        ...formData,
        preferences: {
          investmentMin: parseInt(formData.investmentMin) || 0,
          investmentMax: parseInt(formData.investmentMax) || 0,
          preferredLocations: formData.preferredLocations,
          propertyTypes: formData.propertyTypes,
        },
        updatedAt: new Date().toISOString(),
      };

      console.log('Final data to save:', updatedData);

      // Call the API to update the profile
      const result = await api.users.updateProfile(userProfile.uid, updatedData);
      console.log('Profile update result:', result);

      // Update local state as well
      await updateUserProfile(updatedData);
      console.log('Local state updated');

      console.log('Profile saved successfully - closing modal');
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      location: '',
      bio: '',
      profilePhoto: '',
      investmentMin: '',
      investmentMax: '',
      preferredLocations: [],
      propertyTypes: [],
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: '#008080', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Edit sx={{ mr: 1 }} />
          Edit Profile
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Profile Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Avatar
              src={formData.profilePhoto}
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                bgcolor: '#008080'
              }}
            >
              <Person sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ color: '#008080', fontWeight: 600 }}>
                {formData.name || 'Your Name'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Real Estate Investor
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.location || 'Your Location'}
              </Typography>
            </Box>
          </Box>

          {/* Profile Photo Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
            Profile Photo
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={formData.profilePhoto || undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: '#008080'
                  }}
                >
                  <Person sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Photo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Upload and crop a new photo to update your profile
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Upload & Crop New Photo
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mb: 1 }}
              >
                Choose Photo
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handlePhotoSelect}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Supported formats: JPG, PNG, GIF. Max size: 5MB
              </Typography>
              <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                Photos will be cropped to a square format
              </Typography>
            </Grid>
          </Grid>

          {/* Basic Information */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                error={!!errors.location}
                helperText={errors.location}
              />
            </Grid>
          </Grid>

          {/* Investment Preferences */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
            Investment Preferences
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Investment"
                type="number"
                value={formData.investmentMin}
                onChange={(e) => handleInputChange('investmentMin', e.target.value)}
                error={!!errors.investmentMin}
                helperText={errors.investmentMin}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                placeholder="e.g., 50000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Maximum Investment"
                type="number"
                value={formData.investmentMax}
                onChange={(e) => handleInputChange('investmentMax', e.target.value)}
                error={!!errors.investmentMax}
                helperText={errors.investmentMax}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                placeholder="e.g., 2000000"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Preferred Locations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {formData.preferredLocations.map((location, index) => (
                  <Chip
                    key={index}
                    label={location}
                    onDelete={() => handleLocationRemove(location)}
                    sx={{
                      backgroundColor: '#008080',
                      color: 'white',
                      '&:hover': { backgroundColor: '#005252' }
                    }}
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                label="Add Location"
                placeholder="e.g., San Francisco, Oakland"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLocationAdd(e.target.value);
                    e.target.value = '';
                  }
                }}
                helperText="Press Enter to add location"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Preferred Property Types</InputLabel>
                <Select
                  multiple
                  value={formData.propertyTypes}
                  onChange={handlePropertyTypeChange}
                  label="Preferred Property Types"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          sx={{
                            backgroundColor: '#008080',
                            color: 'white',
                            '& .MuiChip-deleteIcon': {
                              color: 'white',
                              '&:hover': {
                                color: '#f0f0f0'
                              }
                            }
                          }}
                          onDelete={() => {
                            setFormData(prev => ({
                              ...prev,
                              propertyTypes: prev.propertyTypes.filter(type => type !== value)
                            }));
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="Single Family">Single Family</MenuItem>
                  <MenuItem value="Multi-Family">Multi-Family</MenuItem>
                  <MenuItem value="Condo">Condo</MenuItem>
                  <MenuItem value="Townhouse">Townhouse</MenuItem>
                  <MenuItem value="Commercial">Commercial</MenuItem>
                  <MenuItem value="Land">Land</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Bio Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
            About You
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Bio"
            value={formData.bio}
            onChange={(e) => handleInputChange('bio', e.target.value)}
            error={!!errors.bio}
            helperText={errors.bio}
            placeholder="Tell wholesalers about your investment goals and experience..."
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          sx={{ color: '#666' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={<Save />}
          sx={{
            backgroundColor: '#008080',
            '&:hover': { backgroundColor: '#005252' },
            minWidth: 120
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>

      {/* Photo Crop Modal */}
      <PhotoCropModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageSrc={imageSrc}
        onCropComplete={handlePhotoCrop}
      />
    </Dialog>
  );
}
